import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useLists } from './ListContext';
import { Task, TaskContextType, TaskStatus } from './TaskContext/types';
import { useTaskOperations } from './TaskContext/useTaskOperations';

export type { TaskStatus, Task } from './TaskContext/types';

const LOCAL_TASKS_KEY = 'lovable_local_tasks';
const LOCAL_LIST_ID = 'local_list_default';

const TaskContext = createContext<TaskContextType | undefined>(undefined);

export const TaskProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(false);
  const { session, user } = useAuth();
  const { activeListId, lists } = useLists();

  const {
    addTask,
    updateTask,
    deleteTask,
    updateTaskTime,
    reorderTasks,
    updateTaskStatus,
    taskToEdit,
    setTaskToEdit
  } = useTaskOperations(
    tasks,
    setTasks,
    allTasks,
    setAllTasks,
    activeTask,
    setActiveTask,
    session,
    activeListId,
    lists
  );

  // Fetch tasks from Supabase for authenticated users
  const fetchTasks = React.useCallback(async (listId: string) => {
    if (!session) {
      loadFromLocalStorage();
      return;
    }

    setLoading(true);
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', user?.id)
        .eq('list_id', listId)
        .order('position', { ascending: true });

      if (error) throw error;

      const mappedTasks = data.map((task: any, index: number) => ({
        id: task.id,
        title: task.title,
        detail: task.detail,
        status: task.status as TaskStatus,
        created_at: task.created_at,
        dateCreated: new Date(task.created_at),
        user_id: task.user_id,
        list_id: task.list_id,
        totalTimeSpent: task.total_time_spent || 0,
        pomodorosCompleted: task.pomodoros_completed || 0,
        is_active: task.is_active || false,
        position: task.position ?? index,
        priority_quadrant: task.priority_quadrant,
        priority_x: task.priority_x,
        priority_y: task.priority_y
      }));

      setTasks(mappedTasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
      loadFromLocalStorage();
    } finally {
      setLoading(false);
    }
  }, [session, user]);

  const fetchAllTasks = async () => {
    if (!session || !user) {
      setAllTasks([]);
      return;
    }
    
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const mappedTasks = data.map((task: any) => ({
        id: task.id,
        title: task.title,
        detail: task.detail,
        status: task.status as TaskStatus,
        created_at: task.created_at,
        dateCreated: new Date(task.created_at),
        user_id: task.user_id,
        list_id: task.list_id,
        totalTimeSpent: task.total_time_spent || 0,
        pomodorosCompleted: task.pomodoros_completed || 0,
        is_active: task.is_active || false,
        position: task.position || 0,
        priority_quadrant: task.priority_quadrant,
        priority_x: task.priority_x,
        priority_y: task.priority_y
      }));

      setAllTasks(mappedTasks);
    } catch (error) {
      console.error('Error loading all tasks:', error);
      setAllTasks([]);
    }
  };

  const loadFromLocalStorage = () => {
    if (session) return; // Only for non-authenticated users
    
    const savedTasks = localStorage.getItem(LOCAL_TASKS_KEY);
    if (savedTasks) {
      try {
        const parsedTasks = JSON.parse(savedTasks);
        if (Array.isArray(parsedTasks)) {
          // Ensure all local tasks have the local list ID
          const localTasks = parsedTasks.map(task => ({
            ...task,
            list_id: LOCAL_LIST_ID
          }));
          setTasks(localTasks);
        }
      } catch (error) {
        console.error('Failed to parse tasks from localStorage:', error);
      }
    }
  };

  const saveToLocalStorage = (tasksToSave: Task[]) => {
    if (session) return; // Only for non-authenticated users
    
    try {
      localStorage.setItem(LOCAL_TASKS_KEY, JSON.stringify(tasksToSave));
    } catch (error) {
      console.error('Failed to save tasks to localStorage:', error);
    }
  };

  // Save to localStorage when tasks change (only if not authenticated)
  useEffect(() => {
    if (!session && tasks.length > 0) {
      saveToLocalStorage(tasks);
    }
  }, [tasks, session]);

  // Fetch tasks when activeListId or authentication status changes
  useEffect(() => {
    if (activeListId) {
      if (session) {
        fetchTasks(activeListId);
      } else {
        loadFromLocalStorage();
      }
    } else {
      setTasks([]);
    }
  }, [activeListId, session, fetchTasks]);

  // Load all tasks when user changes
  useEffect(() => {
    if (session && user) {
      fetchAllTasks();
    } else {
      setAllTasks([]);
    }
  }, [session, user]);

  const resetTasks = () => {
    setTasks([]);
    setActiveTask(null);
    setTaskToEdit(null);
    
    // Clear localStorage if not authenticated
    if (!session) {
      localStorage.removeItem(LOCAL_TASKS_KEY);
    }
  };

  // Migration function to move local tasks to user account
  const migrateLocalTasksToAccount = async () => {
    if (!session) return;

    const localTasks = localStorage.getItem(LOCAL_TASKS_KEY);
    if (localTasks) {
      try {
        const parsedTasks = JSON.parse(localTasks);
        if (Array.isArray(parsedTasks) && parsedTasks.length > 0) {
          // Clear local storage
          localStorage.removeItem(LOCAL_TASKS_KEY);
          // Tasks will be migrated by the list context when creating the first list
        }
      } catch (error) {
        console.error('Failed to migrate local tasks:', error);
      }
    }
  };

  // Migrate local tasks when user signs in
  useEffect(() => {
    if (session) {
      migrateLocalTasksToAccount();
    }
  }, [session]);

  return (
    <TaskContext.Provider
      value={{
        tasks,
        allTasks,
        activeTask,
        setActiveTask,
        addTask,
        updateTask,
        deleteTask,
        updateTaskTime,
        resetTasks,
        taskToEdit,
        setTaskToEdit,
        reorderTasks,
        updateTaskStatus,
        loading,
        fetchTasks,
        fetchAllTasks,
        migrateLocalTasksToAccount,
        LOCAL_LIST_ID
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};

export const useTasks = () => {
  const context = useContext(TaskContext);
  if (context === undefined) {
    throw new Error('useTasks must be used within a TaskProvider');
  }
  return context;
};

export default TaskProvider;